import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Dropdown from '../../Assets/Dropdown';
import { mlbHigherIsBetter } from '../../../utils/mlbUtils';
import { UpCaret, DownCaret } from '../../common/icons';

const MlbStatAvgs = (props) => {
    const game = props.game;
    const [activeTab, setActiveTab] = useState('batting');
    const showCarets = props.showCarets !== false; // Default to true unless explicitly set to false
    
    // Define color constants
    const BETTER_COLOR = "#43a047"; // Green
    const WORSE_COLOR = "#e53935"; // Red
    
    const battingLabels = {
        "AB":"At Bats",
        "R":"Runs",
        "H":"Hits",
        "HR": "Home Runs",
        "RBI":"RBIs",
        "BB":"Walks Taken",
        "HBP":"Hit By Pitch",
        "SO":"Strike Outs",
        "PA":"Plate Appearances",
        "BA":"Batting Average",
        "OBP":"On Base Percentage",
        "SLG":"Slugging Percentage",
        "OPS":"OPS",
        "Pit":"Pitches Taken",
        "Str":"Strikes Taken",
        "singles":"Singles",
        "doubles":"Doubles",
        "triples":"Triples",
        "SF":"Sac Flies",
        "SB":"Stolen Bases",
        "CS":"Caught Stealing",
        "LOB":"Left on Base",
    };
    const pitchingLabels = {
        "IP":"Innings Pitched",
        "H":"Hits Allowed",
        "R":"Runs Allowed",
        "ER":"Earned Runs",
        "BB":"Batters Walked",
        "SO":"Strike Outs Thrown",
        "HR":"Home Runs Allowed",
        "ERA":"ERA",
        "BF":"Batters Faced",
        "Pit":"Pitches Thrown",
        "Str":"Strikes Thrown"
    };
    
    // Calculate stats with useMemo instead of useState + useEffect
    const battingStats = useMemo(() => {
        if(game && 'batting' in game.home && 'batting' in game.away) {
            const battingKeys = Object.keys(game.home.batting);
            let tmpBattingStats = {};
            battingKeys.forEach(stat=>{
                tmpBattingStats[stat] = [game.home.batting[stat], game.away.batting[stat]];
            });
            return tmpBattingStats;
        }
        return null;
    }, [game]);
    
    const pitchingStats = useMemo(() => {
        if(game && 'pitching' in game.home && 'pitching' in game.away) {
            const pitchingKeys = Object.keys(game.home.pitching);
            let tmpPitchingStats = {};
            pitchingKeys.forEach(stat=>{
                tmpPitchingStats[stat] = [game.home.pitching[stat], game.away.pitching[stat]];
            });
            return tmpPitchingStats;
        }
        return null;
    }, [game]);
    
    // Memoize the compareStats function
    const compareStats = useCallback((stat, homeVal, awayVal) => {
        if (homeVal === awayVal) return null;
        
        // Get the proper key for mlbHigherIsBetter
        const statKey = activeTab === 'batting' ? 'batting.' + stat : 'pitching.' + stat;
        const isHigherBetter = mlbHigherIsBetter[statKey];
        
        if (isHigherBetter) {
            return homeVal > awayVal ? 'home' : 'away';
        } else {
            return homeVal < awayVal ? 'home' : 'away';
        }
    }, [activeTab]);
    
    // Memoize rendering battingStats rows
    const renderBattingRows = useMemo(() => {
        if (!battingStats) return null;
        
        return Object.keys(battingStats).map((stat, statIdx) => {
            const better = showCarets ? compareStats(stat, game.home.batting[stat], game.away.batting[stat]) : null;
            const isHigherBetter = mlbHigherIsBetter['batting.' + stat];
            const isLast = statIdx === Object.keys(battingStats).length - 1;

            return (
                <tr key={stat}>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate py-4 pl-6 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100 sm:pl-8 lg:pl-10`}>
                        {battingLabels[stat]}
                    </td>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate px-3 py-4 text-sm text-gray-900 dark:text-gray-100 ${better === 'home' ? 'font-medium text-green-700 dark:text-green-500' : better === 'away' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                        {game.home.batting[stat]}
                        {better === 'home' && (
                            isHigherBetter ? (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            ) : (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            )
                        )}
                        {better === 'away' && (
                            isHigherBetter ? (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            ) : (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            )
                        )}
                    </td>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate px-3 py-4 text-sm text-gray-900 dark:text-gray-100 ${better === 'away' ? 'font-medium text-green-700 dark:text-green-500' : better === 'home' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                        {game.away.batting[stat]}
                        {better === 'away' && (
                            isHigherBetter ? (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            ) : (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            )
                        )}
                        {better === 'home' && (
                            isHigherBetter ? (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            ) : (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            )
                        )}
                    </td>
                </tr>
            );
        });
    }, [battingStats, compareStats, game, showCarets, BETTER_COLOR, WORSE_COLOR]);
    
    // Memoize rendering pitchingStats rows
    const renderPitchingRows = useMemo(() => {
        if (!pitchingStats) return null;
        
        return Object.keys(pitchingStats).map((stat, statIdx) => {
            const better = showCarets ? compareStats(stat, game.home.pitching[stat], game.away.pitching[stat]) : null;
            const isHigherBetter = mlbHigherIsBetter['pitching.' + stat];
            const isLast = statIdx === Object.keys(pitchingStats).length - 1;
            
            return (
                <tr key={stat}>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate py-4 pl-6 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100 sm:pl-8 lg:pl-10`}>
                        {pitchingLabels[stat]}
                    </td>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate px-3 py-4 text-sm text-gray-900 dark:text-gray-100 ${better === 'home' ? 'font-medium text-green-700 dark:text-green-500' : better === 'away' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                        {game.home.pitching[stat]}
                        {better === 'home' && (
                            isHigherBetter ? (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            ) : (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            )
                        )}
                        {better === 'away' && (
                            isHigherBetter ? (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            ) : (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            )
                        )}
                    </td>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate px-3 py-4 text-sm text-gray-900 dark:text-gray-100 ${better === 'away' ? 'font-medium text-green-700 dark:text-green-500' : better === 'home' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                        {game.away.pitching[stat]}
                        {better === 'away' && (
                            isHigherBetter ? (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            ) : (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            )
                        )}
                        {better === 'home' && (
                            isHigherBetter ? (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            ) : (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            )
                        )}
                    </td>
                </tr>
            );
        });
    }, [pitchingStats, compareStats, game, showCarets, BETTER_COLOR, WORSE_COLOR]);
    
    return(
        <>
            <div className="sm:max-w-[600px] md:max-w-[700px] lg:max-w-[800px] px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between mb-4 relative z-20 mt-1">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Stat Averages</h3>
                    <Dropdown
                        id="stat-type-select"
                        title="Select Stats Type"
                        items={[
                            { value: 'batting', label: 'Batting' },
                            { value: 'pitching', label: 'Pitching' }
                        ]}
                        activeItem={activeTab}
                        onSelect={setActiveTab}
                        size="md"
                        variant="default"
                    />
                </div>
                {activeTab === "batting" && battingStats ? (
                    <div className="mt-8 flow-root">
                        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
                            <div className="inline-block w-full py-2 align-middle">
                                <div className="max-h-[600px] overflow-y-auto shadow ring-1 ring-gray-300 ring-opacity-5 rounded-lg dark:ring-gray-700">
                                    <table className="min-w-full w-full table-fixed border-separate border-spacing-0">
                                        <thead>
                                            <tr>
                                                <th scope="col" className="w-[50%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter sm:pl-8 lg:pl-10 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Stat</th>
                                                <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-4 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{game.home.team}</th>
                                                <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-4 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{game.away.team}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {renderBattingRows}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
                {activeTab === "pitching" && pitchingStats ? (
                    <div className="mt-8 flow-root">
                        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
                            <div className="inline-block w-full py-2 align-middle">
                                <div className="max-h-[600px] overflow-y-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg dark:ring-gray-700">
                                    <table className="min-w-full w-full table-fixed border-separate border-spacing-0">
                                        <thead>
                                            <tr>
                                                <th scope="col" className="w-[50%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 py-3.5 pl-1 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter sm:pl-8 lg:pl-10 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Stat</th>
                                                <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-4 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{game.home.team}</th>
                                                <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-4 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{game.away.team}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {renderPitchingRows}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </>
    );
};

export default MlbStatAvgs;